import { Space, Typography } from 'antd';
import React, {
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react';
import { DASHBOARD_APP_BASE_URL, OCTOPUS_APP_BASE_URL } from '../../../../const/envs';
import { ETourMessageType } from '../../../../enums/tours.enum';
import { ToursContext } from '../../context/tours.context';

const { Text } = Typography;

interface Props {
    selectionMode: boolean;
    onElementSelected: (selector: string) => void;
    isEditingTour?: boolean;
    tourStartUrl?: string;
}

const DashboardMirror = React.forwardRef<HTMLIFrameElement, Props>(({
    selectionMode,
    onElementSelected,
    isEditingTour = false,
    tourStartUrl,
}, ref) => {
    const { currentPath, setCurrentPath } = useContext(ToursContext);
    const internalRef = useRef<HTMLIFrameElement>(null);
    const [isIframeLoaded, setIsIframeLoaded] = useState(false);

    const combinedRef = useCallback((node: HTMLIFrameElement | null) => {
        (internalRef as React.MutableRefObject<HTMLIFrameElement | null>).current = node;
        if (ref) {
            if (typeof ref === 'function') {
                ref(node);
            } else if (ref && 'current' in ref) {
                (ref as React.MutableRefObject<HTMLIFrameElement | null>).current = node;
            }
        }
    }, [ref]);

    const getIframeRef = useCallback(() => internalRef.current, []);

    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            const authorizedOrigins = [
                DASHBOARD_APP_BASE_URL,
                OCTOPUS_APP_BASE_URL,
            ];

            if (!authorizedOrigins.includes(event.origin)) {
                console.warn('Unauthorized origin detected:', event.origin);
                return;
            }

            switch (event.data.type) {
            case ETourMessageType.ELEMENT_SELECTED: {
                const pathSelected = event.data.data.currentPath;
                const cssSelector = event.data.data.cssSelector;
                setCurrentPath(pathSelected);
                onElementSelected(cssSelector);
                break;
            }
            case ETourMessageType.URL_CHANGED: {
                const pathSelected = event.data.data.currentPath;
                setCurrentPath(pathSelected);
                break;
            }
            }
        };

        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, [selectionMode, onElementSelected]);

    useEffect(() => {
        if (isIframeLoaded) {
            const iframe = getIframeRef();
            if (iframe?.contentWindow) {
                iframe.contentWindow.postMessage({
                    type: ETourMessageType.SET_SELECTION_MODE,
                    enabled: selectionMode,
                }, DASHBOARD_APP_BASE_URL);
            }
        }
    }, [selectionMode, isIframeLoaded, getIframeRef]);

    useEffect(() => {
        if (isIframeLoaded && isEditingTour && tourStartUrl) {
            const iframe = getIframeRef();
            if (iframe?.contentWindow) {
                iframe.contentWindow.postMessage({
                    type: ETourMessageType.URL_EDIT_PREVIEW,
                    startUrl: tourStartUrl,
                }, DASHBOARD_APP_BASE_URL);
            }
        }
    }, [isIframeLoaded, isEditingTour, tourStartUrl, getIframeRef]);

    const handleIframeLoad = () => {
        setIsIframeLoaded(true);
    };

    return (
        <Space direction="vertical" style={{ width: '100%' }}>
            <Space
                style={{
                    width: '100%',
                    padding: '8px 12px',
                    backgroundColor: '#f5f5f5',
                    border: '1px solid #d9d9d9',
                    borderRadius: '6px 6px 0 0',
                    fontSize: '12px',
                }}
            >
                <Text strong style={{ color: '#666' }}>
                    Current Path:
                </Text>
                <Text code style={{ fontSize: '12px', color: '#666' }}>
                    {currentPath}
                </Text>
            </Space>

            <div style={{
                position: 'relative',
                height: '70vh',
                border: '1px solid #d9d9d9',
                borderTop: 'none',
                borderRadius: '0 0 6px 6px',
                overflow: 'hidden',
            }}
            >
                <iframe
                    ref={combinedRef}
                    src={DASHBOARD_APP_BASE_URL}
                    width="100%"
                    height="100%"
                    frameBorder="0"
                    title="Dashboard Preview"
                    onLoad={handleIframeLoad}
                    style={{
                        pointerEvents: 'auto',
                    }}
                />

                {selectionMode && (
                    <>
                        <Space
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                zIndex: 5,
                                pointerEvents: 'none',
                                border: '2px solid #52c41a',
                                borderRadius: '6px',
                            }}
                        />

                        <Space
                            style={{
                                position: 'absolute',
                                top: 10,
                                left: 10,
                                backgroundColor: 'rgba(82, 196, 26, 0.95)',
                                color: 'white',
                                padding: '8px 12px',
                                borderRadius: '4px',
                                fontSize: '12px',
                                zIndex: 20,
                                fontWeight: '500',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                            }}
                        >
                            <Text style={{ color: 'white', fontSize: '12px', fontWeight: '500' }}>
                                🎯 Selection mode active - Click any element in the dashboard
                            </Text>
                        </Space>
                    </>
                )}

                {!isIframeLoaded && (
                    <Space
                        style={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            textAlign: 'center',
                            color: '#666',
                        }}
                    >
                        <Text style={{ color: '#666' }}>
                            Loading dashboard...
                        </Text>
                    </Space>
                )}
            </div>
        </Space>
    );
});

DashboardMirror.displayName = 'DashboardMirror';

export default DashboardMirror;
